/**
 * HollyQuickReplies — horizontal row of chip buttons.
 *
 * Rendered from suggestedCtas in Holly's response.
 *   needsUserInput=true  → quick reply chip (user types-equivalent text)
 *   needsUserInput=false → action button (parent decides: send-as-text or
 *                          handle as a side-effect like navigation)
 *
 * The parent receives the full CTA object so it can branch on `cta.action`
 * (e.g. `tp_continue_booking` navigates to the main Flox /register page rather
 * than echoing the label back as a chat message).
 */

import type { HollySuggestedCta } from './types';

interface HollyQuickRepliesProps {
  ctas: HollySuggestedCta[];
  onSelect: (cta: HollySuggestedCta) => void;
  disabled?: boolean;
}

export default function HollyQuickReplies({
  ctas,
  onSelect,
  disabled = false,
}: HollyQuickRepliesProps) {
  if (ctas.length === 0) return null;

  return (
    <div
      className='flex flex-wrap gap-2 px-4 py-2'
      role='group'
      aria-label='Suggested replies'
    >
      {ctas.map((cta) => (
        <button
          key={`${cta.message}::${cta.action ?? ''}`}
          type='button'
          disabled={disabled}
          onClick={() => onSelect(cta)}
          style={{
            padding: '6px 14px',
            borderRadius: 20,
            border: '1px solid #3574E3',
            backgroundColor: cta.needsUserInput ? 'transparent' : '#3574E3',
            color: cta.needsUserInput ? '#3574E3' : '#FFFFFF',
            fontSize: 13,
            fontWeight: 500,
            fontFamily: "'Poppins', sans-serif",
            cursor: disabled ? 'default' : 'pointer',
            transition: `background-color var(--duration-instant) var(--ease-out), transform var(--duration-instant) var(--ease-out)`,
            opacity: disabled ? 0.5 : 1,
          }}
          onMouseEnter={(e) => {
            if (!disabled && cta.needsUserInput) {
              e.currentTarget.style.backgroundColor = 'rgba(53,116,227,0.08)';
            }
          }}
          onMouseLeave={(e) => {
            if (!disabled && cta.needsUserInput) {
              e.currentTarget.style.backgroundColor = 'transparent';
            }
          }}
        >
          {cta.message}
        </button>
      ))}
    </div>
  );
}
